import { fetchLereDynamicPricingLabDecision } from 'actions/SystemActions'
import type { AppDispatch } from 'src/client/store'
import asyncPoll from 'lib/promise/asyncPoll'
import {
  getLereDynamicPricingGroupFromLabFlags,
} from 'checkout/lib/utils/lereDynamicPricing'
import {
  lereDecisionSessionState,
  type LereDynamicPricingAuthSession,
} from 'lib/lere/lereDynamicPricingLabSession'

const LERE_GROUP_POLL_MAX_TIME_MS = 3_000
const LERE_GROUP_POLL_INTERVAL_MS = 100

const decidePromises: Partial<Record<LereDynamicPricingAuthSession, Promise<string | undefined>>> = {}

async function pollForAssignedLereGroup(
  getLereGroup: () => string | undefined,
): Promise<string | undefined> {
  try {
    return await asyncPoll({
      apiCall: async() => getLereGroup(),
      validateFunction: async(assignedLereGroup) => !!assignedLereGroup,
      maxTime: LERE_GROUP_POLL_MAX_TIME_MS,
      pollInterval: LERE_GROUP_POLL_INTERVAL_MS,
    })
  } catch {
    return undefined
  }
}

export function resetLereGroupAssignmentPromises(authSession?: LereDynamicPricingAuthSession): void {
  if (authSession) {
    delete decidePromises[authSession]
    return
  }

  delete decidePromises.loggedIn
  delete decidePromises.loggedOut
}

interface EnsureLereGroupAssignmentParams {
  dispatch: AppDispatch
  authSession: LereDynamicPricingAuthSession
  getLereGroup: () => string | undefined
  hasStitchedDistinctId: boolean
}

export async function ensureLereGroupAssignment({
  dispatch,
  authSession,
  getLereGroup,
  hasStitchedDistinctId,
}: EnsureLereGroupAssignmentParams): Promise<string | undefined> {
  const existingLereGroup = getLereGroup()
  if (existingLereGroup) {
    return existingLereGroup
  }

  if (!hasStitchedDistinctId) {
    return undefined
  }

  if (lereDecisionSessionState[authSession].completed) {
    return undefined
  }

  const existingPromise = decidePromises[authSession]
  if (existingPromise) {
    return existingPromise
  }

  if (lereDecisionSessionState[authSession].requested) {
    return pollForAssignedLereGroup(getLereGroup)
  }

  lereDecisionSessionState[authSession].requested = true

  decidePromises[authSession] = new Promise<string | undefined>((resolve) => {
    dispatch(fetchLereDynamicPricingLabDecision({
      requireStitched: true,
      onComplete: (labFlags) => {
        lereDecisionSessionState[authSession].completed = true
        delete decidePromises[authSession]
        resolve(getLereDynamicPricingGroupFromLabFlags(labFlags))
      },
    }))
  })

  return decidePromises[authSession]
}
